import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { combineLatest, Observable, skipWhile } from 'rxjs';
import { map, filter, switchMap, distinctUntilChanged } from 'rxjs/operators';
import { EmailMessage } from 'common/store/genesys-cloud/conversations/emails/models/email-message.model';
import { EmailAddress } from 'common/store/genesys-cloud/conversations/emails/models/email-address.model';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import {
  deleteAttachment,
  sendEmailMessage,
  updateEmailDraft
} from 'common/store/genesys-cloud/conversations/emails/actions/email-message.actions';
import { WrapUpCode } from 'common/store/genesys-cloud/conversations/common/models/wrap-up-code.model';
import { closeEmailConversation } from 'common/store/genesys-cloud/conversations/emails/actions/email-conversation.actions';
import {
  selectEmailByMessageId,
  selectEmailError,
  selectIsConversationAcceptable,
  selectIsConversationOpen,
  selectIsConversationTransmitting
} from 'common/store/genesys-cloud/conversations/emails/selectors/email-conversation.selectors';
import * as EmailMessageActions from 'common/store/genesys-cloud/conversations/emails/actions/email-message.actions';
import { GenesysEmailOutgoingHeader } from 'shared/components/genesys-email-outgoing-header/models/genesys-email-outgoing-header.model';
import { slideInFromBottomAnimation } from 'shared/components/work-widget-control-panel/scss/slide-in-from-bottom-animation';
import { ClosingConfirmationService } from 'shared/components/confirmation-modal/confirmation-modal.service';
import { DestroySubjectBaseDirective } from 'shared/directives/destroy-subject-base/destroy-subject-base.directive';
import { GenesysEmailFooterStore } from '../genesys-email-footer.store';
import { GenesysEmailComposeService } from '../services/genesys-email-compose.service';
import { ConnectionState } from 'common/store/genesys-cloud/conversations/common/models/connection-state.enum';

@Component({
  selector: 'app-genesys-email-footer',
  templateUrl: './genesys-email-footer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./genesys-email-footer.component.scss'],
  animations: [slideInFromBottomAnimation],
  providers: [GenesysEmailFooterStore]
})
export class GenesysEmailFooterComponent extends DestroySubjectBaseDirective implements OnInit {
  isInteractionDisabled$: Observable<boolean>;

  private readonly _isInteractionsEnabled$ = this._emailFooterStore.isInteractionsEnabled$;

  readonly footerMode$ = this._emailFooterStore.footerMode$;
  readonly isMoreActionsVisible$ = this._emailFooterStore.isMoreActionsVisible$;
  readonly editorMessages$ = this._emailFooterStore.editorMessages$;
  readonly composedEmail$ = this._emailFooterStore.composedEmail$;
  readonly isConfirmationModalVisible$ = this._emailFooterStore.isConfirmationModalVisible$;
  readonly isQuotedMessageAdded$ = this._emailFooterStore.isQuotedMessageAdded$;
  readonly selectedWrapUpCode$ = this._emailFooterStore.selectedWrapUpCode$;

  @Input() conversationId: string;
  @Input() isFocused: boolean;
  @Input() isExpanded: boolean;
  @Input() lastEmail: EmailMessage;
  @Input() isLastEmailFromCustomer: boolean;
  @Input() agentEmailAddress: EmailAddress;
  @Input() draftEmail: EmailMessage;
  @Input() emailError: string;
  @Output()
  readonly openTransferPanel = new EventEmitter<void>();

  constructor(
    private readonly _closingConfirmationService: ClosingConfirmationService,
    private readonly store: Store,
    private readonly _emailFooterStore: GenesysEmailFooterStore,
    private readonly _actions: Actions,
    private readonly _emailComposeService: GenesysEmailComposeService
  ) {
    super();
  }

  ngOnInit() {
    this._initStoreState();

    this._initInteractionsDisabled();

    this._initActionSubscriptions();

    this._initHandleEditorMessageBasedOnEmailStateSubscription();

    this._initConversationOpenSubsciption();
  }

  selectWrapUp(wrapUpCode: WrapUpCode): void {
    this._emailFooterStore.setSelectedWrapUpCode(wrapUpCode);
  }

  onHeaderSave(newModel: GenesysEmailOutgoingHeader): void {
    this._emailFooterStore.storeComposedEmail({ ...newModel });
    this._emailFooterStore.storeFooterMode('EDITOR');
  }

  onHeaderCancel(): void {
    this._emailFooterStore.cancelFooterMode();
  }

  onEditorContentChanged({ htmlBody, textBody }: { htmlBody: string; textBody: string }) {
    this._emailFooterStore.storeComposedEmail({ htmlBody, textBody });
  }

  onSend(composedEmail: Partial<EmailMessage>): void {
    const matches = this._emailComposeService.getAttachmentMatches(
      this.lastEmail?.attachments,
      this.draftEmail?.attachments
    );
    const emailMessage = {
      ...composedEmail,
      attachments: this.draftEmail?.attachments || [],
      time: moment().toISOString(),
      historyIncluded: false,
      htmlBody: matches.size
        ? this._emailComposeService.replaceContentUri(composedEmail.htmlBody, matches)
        : composedEmail.htmlBody
    };

    const emailError = this._emailComposeService.getEmailValidatorErrorMessage(emailMessage);
    if (emailError) {
      this._emailFooterStore.setEditorMessages(emailError);
    } else {
      this._emailFooterStore.setInteractionsEnabled(false);
      this.store.dispatch(sendEmailMessage({ conversationId: this.conversationId, emailMessage }));
    }
  }

  onReply(): void {
    const defaultComposedEmail = this._emailComposeService.getReplyDefaultComposedEmail(
      this.lastEmail,
      this.agentEmailAddress,
      this.draftEmail,
      this.isLastEmailFromCustomer
    );
    this._emailFooterStore.storeComposedEmail(defaultComposedEmail);
    if (this.isLastEmailFromCustomer) {
      this._emailFooterStore.storeFooterMode('EDITOR');
    } else {
      this._emailFooterStore.clearEditorMessages();
      this._emailFooterStore.addEditorMessage('genesysEmailFooter.replyToOutboundEmail');
      this.openOutgoingHeaderPanel();
    }
  }

  onReplyAll(): void {
    const defaultComposedEmail = this._emailComposeService.getReplyAllDefaultComposedEmail(
      this.lastEmail,
      this.agentEmailAddress,
      this.draftEmail,
      this.isLastEmailFromCustomer
    );
    this._emailFooterStore.storeComposedEmail(defaultComposedEmail);
    this.openOutgoingHeaderPanel();
  }

  onForward(): void {
    const defaultComposedEmail = this._emailComposeService.getForwardDefaultComposedEmail(
      this.lastEmail.subject,
      this.agentEmailAddress,
      this.draftEmail
    );
    this._emailFooterStore.storeComposedEmail(defaultComposedEmail);
    this.openOutgoingHeaderPanel();
  }

  onOpenWrapUpPanel() {
    this._emailFooterStore.storeFooterMode('WRAPUP');
  }

  onCancelFooterMode() {
    this._emailFooterStore.cancelFooterMode();
  }

  closeConfirmation(): void {
    this._emailFooterStore.setIsConfirmationModalVisible(false);
  }

  openOutgoingHeaderPanel(): void {
    this._emailFooterStore.storeFooterMode('RECIPIENTS');
  }

  onReplyCancel(): void {
    this._emailFooterStore.resetState(this.conversationId);
    this.store.dispatch(
      updateEmailDraft({ conversationId: this.conversationId, draftMessage: { htmlBody: '', textBody: '' } })
    );

    if (this.draftEmail?.attachments?.length) {
      this._deleteDraftAttachments();
    }
  }

  handleConfirmation(): void {
    const isConfirmationModalDisabled = <boolean>(
      this._closingConfirmationService.getClosingConfirmationSetting(WorkItemTypes.GENESYS_EMAIL)
    );
    if (isConfirmationModalDisabled) {
      this.store.dispatch(closeEmailConversation({ conversationId: this.conversationId }));
    } else {
      this._emailFooterStore.setIsConfirmationModalVisible(true);
    }
  }

  toggleMoreActions(): void {
    this._emailFooterStore.toggleMoreActionsVisible();
  }

  clearEditorMessages(): void {
    this._emailFooterStore.clearEditorMessages();
  }

  addQuotedContent(htmlBody: string): void {
    this._emailFooterStore.setIsQuotedMessageAdded(true);
    this._emailFooterStore.storeComposedEmail({ htmlBody });
  }

  onAttachmentSizeLimitError(sizeLimit: number) {
    this._emailFooterStore.setEditorMessages('bcEmailBox.attachmentOverSizeLimitMessage', {
      sizeLimit
    });
  }

  onOpenTransferPanel() {
    this.openTransferPanel.emit();
  }

  private _initStoreState() {
    this._emailFooterStore.initConversationId(this.conversationId);
    const initFooterMode = this.emailError ? 'EDITOR' : null;
    this._emailFooterStore.initFooterMode(initFooterMode);

    const defaultComposedEmail = this._emailComposeService.getDefaultComposedEmail(
      this.draftEmail,
      this.agentEmailAddress
    );
    this._emailFooterStore.initComposedEmail(defaultComposedEmail);
  }

  private _initActionSubscriptions(): void {
    const filterConversation = ({ conversationId }: { conversationId: string }) =>
      conversationId === this.conversationId;

    this._actions
      .pipe(ofType(EmailMessageActions.attachmentUploadSuccess), filter(filterConversation), this.takeUntilDestroy())
      .subscribe(() => this._emailFooterStore.removeEditorMessage('genesysEmailFooter.uploadingAttachment'));

    this._actions
      .pipe(ofType(EmailMessageActions.attachmentUploadFailure), filter(filterConversation), this.takeUntilDestroy())
      .subscribe(() => this._emailFooterStore.setEditorMessages('genesysEmailFooter.attachmentUploadError'));

    this._actions
      .pipe(ofType(EmailMessageActions.attachmentDeleteFailure), filter(filterConversation), this.takeUntilDestroy())
      .subscribe(() => this._emailFooterStore.setEditorMessages('genesysEmailFooter.attachmentDeleteError'));

    this._actions
      .pipe(
        ofType(EmailMessageActions.sendEmailMessageFailure),
        filter(({ conversationId }) => filterConversation({ conversationId })),
        this.takeUntilDestroy()
      )
      .subscribe(({ errorMessage }) => {
        this._emailFooterStore.setEditorMessages(errorMessage || 'genesysEmailFooter.sendingEmailError');
        this._emailFooterStore.setInteractionsEnabled(true);
      });

    this._actions
      .pipe(
        ofType(EmailMessageActions.sendEmailMessageSuccess),
        filter(({ conversationId }) => filterConversation({ conversationId })),
        switchMap(({ conversationId, emailMessage }) =>
          this.store.select(selectEmailByMessageId(conversationId, emailMessage.id))
        ),
        skipWhile(({ email }) => email?.state === ConnectionState.Connected && !email?.errorInfo),
        filter(({ email }) => !email?.errorInfo && email?.state === ConnectionState.Connected),
        distinctUntilChanged(({ email }, { email: newEmail }) => email.id === newEmail.id),
        this.takeUntilDestroy()
      )
      .subscribe(({ conversationId }) => {
        this._emailFooterStore.resetState(conversationId);
      });

    this._actions
      .pipe(ofType(EmailMessageActions.uploadAttachment), filter(filterConversation), this.takeUntilDestroy())
      .subscribe(() => this._emailFooterStore.setEditorMessages('genesysEmailFooter.uploadingAttachment'));
  }

  private _initInteractionsDisabled(): void {
    const isAcceptable$ = this.store.select(selectIsConversationAcceptable(this.conversationId));

    this.isInteractionDisabled$ = combineLatest([this._isInteractionsEnabled$, isAcceptable$]).pipe(
      map(([isInteractionsEnabled, isAcceptable]) => !isInteractionsEnabled || isAcceptable)
    );
  }

  private _initHandleEditorMessageBasedOnEmailStateSubscription() {
    const isTransmitting$ = this.store.select(selectIsConversationTransmitting(this.conversationId));
    const emailError$ = this.store.select(selectEmailError(this.conversationId));

    combineLatest([isTransmitting$, emailError$])
      .pipe(this.takeUntilDestroy())
      .subscribe(([isTransmitting, emailError]) => {
        this._emailFooterStore.setInteractionsEnabled(!isTransmitting);
        if (emailError) {
          this._emailFooterStore.storeFooterMode('EDITOR');
          this._emailFooterStore.setEditorMessages(emailError);
        } else if (isTransmitting) {
          this._emailFooterStore.setEditorMessages('genesysEmailFooter.sendingEmail');
        } else {
          this._emailFooterStore.clearEditorMessages();
        }
      });
  }

  private _initConversationOpenSubsciption() {
    const isConversationOpen$ = this.store.select(selectIsConversationOpen(this.conversationId));

    isConversationOpen$
      .pipe(
        filter((isConversationOpen) => !isConversationOpen),
        this.takeUntilDestroy()
      )
      .subscribe(() => {
        this._emailFooterStore.setInteractionsEnabled(false);
        this._emailFooterStore.storeFooterMode('WRAPUP');
      });
  }

  private _deleteDraftAttachments() {
    this.draftEmail?.attachments.forEach((value) => {
      this.store.dispatch(deleteAttachment({ conversationId: this.conversationId, attachmentMeta: value }));
    });
  }
}
